/* Ardor Banner Builder - 3D banner preview (WebGL). Ardor Printing. */
(function () {
  'use strict';
  function createEngine(canvas, host) {
    const self = host;
    let gl = null;
    try { gl = canvas.getContext('webgl', { antialias: true, alpha: true, premultipliedAlpha: true, preserveDrawingBuffer: false }); } catch (e) { gl = null; }
    if (!gl) return null;
    const aniso = gl.getExtension('EXT_texture_filter_anisotropic') || gl.getExtension('WEBKIT_EXT_texture_filter_anisotropic');
    const PI = Math.PI, TAU = PI * 2;

    const M = {
      ident() { const m = new Float32Array(16); m[0] = m[5] = m[10] = m[15] = 1; return m; },
      mul(a, b) { const o = new Float32Array(16); for (let c = 0; c < 4; c++) for (let r = 0; r < 4; r++) { let s = 0; for (let k = 0; k < 4; k++) s += a[k * 4 + r] * b[c * 4 + k]; o[c * 4 + r] = s; } return o; },
      persp(fov, asp, n, f) { const t = 1 / Math.tan(fov / 2), o = new Float32Array(16); o[0] = t / asp; o[5] = t; o[10] = (f + n) / (n - f); o[11] = -1; o[14] = 2 * f * n / (n - f); return o; },
      trans(x, y, z) { const m = M.ident(); m[12] = x; m[13] = y; m[14] = z; return m; },
      scale(x, y, z) { const m = M.ident(); m[0] = x; m[5] = y; m[10] = z; return m; },
      rotY(a) { const m = M.ident(), c = Math.cos(a), s = Math.sin(a); m[0] = c; m[2] = -s; m[8] = s; m[10] = c; return m; },
      rotX(a) { const m = M.ident(), c = Math.cos(a), s = Math.sin(a); m[5] = c; m[6] = s; m[9] = -s; m[10] = c; return m; },
      rotZ(a) { const m = M.ident(), c = Math.cos(a), s = Math.sin(a); m[0] = c; m[1] = s; m[4] = -s; m[5] = c; return m; },
      alignZ(n) {
        const d = n[2];
        if (d > 0.99999) return M.ident();
        if (d < -0.99999) return M.rotX(PI);
        const l = Math.hypot(n[0], n[1]); const x = -n[1] / l, y = n[0] / l, z = 0;
        const ang = Math.acos(d), c = Math.cos(ang), s = Math.sin(ang), t = 1 - c;
        const m = M.ident();
        m[0] = t * x * x + c; m[1] = t * x * y + s * z; m[2] = t * x * z - s * y;
        m[4] = t * x * y - s * z; m[5] = t * y * y + c; m[6] = t * y * z + s * x;
        m[8] = t * x * z + s * y; m[9] = t * y * z - s * x; m[10] = t * z * z + c;
        return m;
      },
      nmat(m) { return new Float32Array([m[0], m[1], m[2], m[4], m[5], m[6], m[8], m[9], m[10]]); }
    };

    const COMMON = `
      vec3 aces(vec3 x) { return clamp((x * (2.51 * x + 0.03)) / (x * (2.43 * x + 0.59) + 0.14), 0.0, 1.0); }
      float sbox(vec3 r, vec3 c, float size, float rough) { float d = dot(r, normalize(c)); float e = cos(size); float b = 0.015 + rough * 0.5; return smoothstep(e - b, e + b * 0.5, d); }
      vec3 env(vec3 r, float rough) {
        vec3 sky = vec3(0.97, 0.97, 0.96); vec3 gnd = vec3(0.34, 0.33, 0.31);
        vec3 c = mix(gnd, sky, smoothstep(-0.35, 0.6, r.y)) * 0.6;
        float k = 1.0 - rough * 0.7;
        c += vec3(1.0, 0.98, 0.95) * sbox(r, vec3(-0.55, 0.55, 0.62), 0.33, rough) * 2.4 * k;
        c += vec3(0.9, 0.94, 1.0) * sbox(r, vec3(0.75, 0.22, 0.62), 0.26, rough) * 1.1 * k;
        c += vec3(1.0) * sbox(r, vec3(0.0, 0.96, -0.25), 0.45, rough) * 0.9 * k;
        return c;
      }
      float ggx(vec3 N, vec3 V, vec3 L, float rough) {
        vec3 H = normalize(L + V);
        float a = max(rough * rough, 0.002); float a2 = a * a;
        float nh = max(dot(N, H), 0.0); float nl = max(dot(N, L), 0.0); float nv = max(dot(N, V), 0.001);
        float dd = nh * nh * (a2 - 1.0) + 1.0; float D = a2 / (3.14159 * dd * dd);
        float k = (rough + 1.0) * (rough + 1.0) / 8.0;
        float G = (nl / (nl * (1.0 - k) + k)) * (nv / (nv * (1.0 - k) + k));
        float F = 0.04 + 0.96 * pow(1.0 - max(dot(H, V), 0.0), 5.0);
        return D * G * F / (4.0 * nl * nv + 0.001) * nl;
      }
    `;

    const VS_BANNER = `
      attribute vec2 aUV;
      uniform mat4 uProj; uniform mat4 uView; uniform mat4 uModel;
      uniform vec2 uSize; uniform float uTime; uniform float uAmp; uniform float uPinTop; uniform float uPinBot; uniform float uBandT; uniform float uBandB; uniform float uRip;
      varying vec2 vUV; varying vec3 vN; varying vec3 vW;
      const float TAU = 6.2831853;
      float disp(vec2 q) {
        float L = max(uSize.x, uSize.y);
        float x = (q.x - 0.5) * uSize.x; float y = (q.y - 0.5) * uSize.y;
        float d = sin(x * TAU / (L * 1.1) + uTime * 0.9) * 0.55
                + sin(x * TAU / (L * 0.55) + y * TAU / (L * 0.8) - uTime * 1.25) * 0.3
                + sin(y * TAU / (L * 0.7) + x * TAU / (L * 1.6) + uTime * 0.7) * 0.15;
        float pin = 1.0;
        if (uPinTop > 0.5) pin *= clamp((1.0 - q.y - uBandT) / 0.45, 0.0, 1.0);
        if (uPinBot > 0.5) pin *= clamp((q.y - uBandB) / 0.45, 0.0, 1.0);
        float r = 0.0;
        if (uRip > 0.0) {
          float ed = min(min(q.x * uSize.x, (1.0 - q.x) * uSize.x), min(q.y * uSize.y, (1.0 - q.y) * uSize.y));
          r = uRip * 0.011 * clamp(ed / 0.3, 0.0, 1.0) * (0.6 * sin(x * TAU / 0.85 + y * TAU / 2.6 + 0.4 * sin(y * TAU / 1.3)) + 0.4 * sin(x * TAU / 0.47 - y * TAU / 1.9 + 1.7));
        }
        return uAmp * d * pin + r * pin;
      }
      void main() {
        vec2 q = aUV;
        vec3 p = vec3((q.x - 0.5) * uSize.x, (q.y - 0.5) * uSize.y, disp(q));
        float e = 0.004;
        float dx = (disp(q + vec2(e, 0.0)) - disp(q - vec2(e, 0.0))) / (2.0 * e * uSize.x);
        float dy = (disp(q + vec2(0.0, e)) - disp(q - vec2(0.0, e))) / (2.0 * e * uSize.y);
        vec3 n = normalize(vec3(-dx, -dy, 1.0));
        vec4 w = uModel * vec4(p, 1.0);
        vW = w.xyz;
        vN = mat3(uModel[0].xyz, uModel[1].xyz, uModel[2].xyz) * n;
        vUV = q;
        gl_Position = uProj * uView * w;
      }
    `;

    const FS_BANNER = `
      precision highp float;
      uniform sampler2D uFront; uniform sampler2D uBack;
      uniform vec3 uCam; uniform vec2 uSize;
      uniform float uRough; uniform float uSheen; uniform float uWrap;
      uniform float uWeave; uniform float uWeaveType; uniform float uWeaveFreq;
      uniform float uMesh; uniform float uMeshFreq; uniform float uMeshFade; uniform float uHemIn;
      uniform float uEmit; uniform vec3 uTX; uniform vec3 uTY;
      varying vec2 vUV; varying vec3 vN; varying vec3 vW;
      ${COMMON}
      void main() {
        vec2 uv = vUV;
        bool fr = gl_FrontFacing;
        vec4 tex = fr ? texture2D(uFront, uv) : texture2D(uBack, uv);
        float alpha = tex.a;
        vec2 inch = uv * uSize * 12.0;
        float edge = min(min(inch.x, uSize.x * 12.0 - inch.x), min(inch.y, uSize.y * 12.0 - inch.y));
        if (uMesh > 0.5 && edge > uHemIn) {
          vec2 g = abs(fract(inch * uMeshFreq / 12.0) - 0.5);
          float m = max(g.x, g.y);
          float hole = 1.0 - smoothstep(0.24, 0.33, m);
          float open = mix(hole * 0.96, 0.24, uMeshFade);
          alpha *= 1.0 - open;
        }
        if (uMesh < 0.5) { if (alpha < 0.5) discard; alpha = 1.0; }
        if (alpha < 0.01) discard;
        vec3 base = pow(tex.rgb, vec3(2.2));
        if (uSheen > 0.0) { float lum = dot(base, vec3(0.2126, 0.7152, 0.0722)); base = mix(base, vec3(lum), 0.07) * 0.94 + 0.012; }
        vec3 N = normalize(vN); if (!fr) N = -N;
        vec3 V = normalize(uCam - vW);
        if (uWeave > 0.001) {
          vec2 w = inch * uWeaveFreq / 12.0 * 6.2831853;
          float bx; float by;
          if (uWeaveType < 0.5) { bx = cos(w.x + w.y); by = cos(w.x - w.y); }
          else { bx = cos(w.x) * (0.6 + 0.4 * sin(w.y)); by = cos(w.y) * (0.6 + 0.4 * sin(w.x)); }
          N = normalize(N + (uTX * bx + uTY * by) * uWeave * 0.12);
        }
        vec3 L1 = normalize(vec3(-0.45, 0.62, 0.64)); vec3 C1 = vec3(1.0, 0.96, 0.9) * 2.35;
        vec3 L2 = normalize(vec3(0.78, 0.18, 0.6)); vec3 C2 = vec3(0.82, 0.88, 1.0) * 0.85;
        vec3 L3 = normalize(vec3(0.15, 0.55, -0.82)); vec3 C3 = vec3(1.0) * 1.0;
        float w1 = max((dot(N, L1) + uWrap) / (1.0 + uWrap), 0.0);
        float w2 = max((dot(N, L2) + uWrap) / (1.0 + uWrap), 0.0);
        float w3 = max((dot(N, L3) + uWrap) / (1.0 + uWrap), 0.0);
        vec3 amb = mix(vec3(0.27, 0.26, 0.25), vec3(0.62, 0.63, 0.65), N.y * 0.5 + 0.5) * 0.55;
        vec3 diff = base * (C1 * w1 + C2 * w2 + C3 * w3 * 0.35 + amb);
        vec3 spec = C1 * ggx(N, V, L1, uRough) + C2 * ggx(N, V, L2, uRough) + C3 * ggx(N, V, L3, uRough) * 0.6;
        float nv = max(dot(N, V), 0.0);
        float fres = 0.04 + 0.96 * pow(1.0 - nv, 5.0);
        vec3 envc = env(reflect(-V, N), uRough) * fres * (1.0 - uRough * 0.55);
        vec3 sheen = vec3(0.0);
        if (uSheen > 0.0) sheen = (C1 * w1 + C2 * w2 + amb) * uSheen * pow(1.0 - nv, 4.0) * 0.5;
        vec3 col = diff * 0.96 + spec * (1.0 - uSheen * 0.6) + envc + sheen;
        if (uEmit > 0.0) col = col * (1.0 - uEmit * 0.5) + base * uEmit * 2.4;
        col = aces(col * 0.92);
        col = pow(col, vec3(1.0 / 2.2));
        gl_FragColor = vec4(col * alpha, alpha);
      }
    `;

    const VS_MESH = `
      attribute vec3 aPos; attribute vec3 aNor;
      uniform mat4 uProj; uniform mat4 uView; uniform mat4 uModel; uniform mat3 uNorm;
      varying vec3 vN; varying vec3 vW;
      void main() { vec4 w = uModel * vec4(aPos, 1.0); vW = w.xyz; vN = uNorm * aNor; gl_Position = uProj * uView * w; }
    `;

    const FS_MESH = `
      precision highp float;
      uniform vec3 uCam; uniform vec3 uColor; uniform float uRough; uniform float uMetal;
      varying vec3 vN; varying vec3 vW;
      ${COMMON}
      void main() {
        vec3 N = normalize(vN); vec3 V = normalize(uCam - vW);
        if (dot(N, V) < 0.0) N = -N;
        vec3 base = uColor;
        vec3 L1 = normalize(vec3(-0.45, 0.62, 0.64)); vec3 C1 = vec3(1.0, 0.96, 0.9) * 2.35;
        vec3 L2 = normalize(vec3(0.78, 0.18, 0.6)); vec3 C2 = vec3(0.82, 0.88, 1.0) * 0.85;
        float w1 = max(dot(N, L1), 0.0); float w2 = max(dot(N, L2), 0.0);
        vec3 amb = mix(vec3(0.27, 0.26, 0.25), vec3(0.62, 0.63, 0.65), N.y * 0.5 + 0.5) * 0.55;
        vec3 diff = base * (1.0 - uMetal) * (C1 * w1 + C2 * w2 + amb);
        vec3 specTint = mix(vec3(1.0), base / 0.04, uMetal);
        vec3 spec = (C1 * ggx(N, V, L1, uRough) + C2 * ggx(N, V, L2, uRough)) * specTint;
        float nv = max(dot(N, V), 0.0);
        float fres = 0.04 + 0.96 * pow(1.0 - nv, 5.0);
        vec3 envc = env(reflect(-V, N), uRough) * mix(vec3(fres), base, uMetal) * (1.0 - uRough * 0.5);
        vec3 col = aces((diff + spec + envc) * 0.92);
        gl_FragColor = vec4(pow(col, vec3(1.0 / 2.2)), 1.0);
      }
    `;

    const VS_SHADOW = `
      attribute vec2 aP;
      uniform mat4 uProj; uniform mat4 uView; uniform vec3 uC; uniform vec2 uR; uniform float uYaw;
      varying vec2 vP;
      void main() {
        vP = aP;
        float c = cos(uYaw); float s = sin(uYaw);
        vec2 q = vec2(aP.x * uR.x, aP.y * uR.y);
        vec3 w = vec3(uC.x + q.x * c + q.y * s, uC.y, uC.z - q.x * s + q.y * c);
        gl_Position = uProj * uView * vec4(w, 1.0);
      }
    `;
    const FS_SHADOW = `
      precision mediump float;
      uniform float uO; varying vec2 vP;
      void main() { float d = length(vP); float a = pow(clamp(1.0 - d, 0.0, 1.0), 1.7) * uO; gl_FragColor = vec4(0.0, 0.0, 0.0, a); }
    `;

    const compile = (type, src) => {
      const sh = gl.createShader(type); gl.shaderSource(sh, src); gl.compileShader(sh);
      if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) { const log = gl.getShaderInfoLog(sh); gl.deleteShader(sh); throw new Error('Shader: ' + log); }
      return sh;
    };
    const program = (vs, fs, attribs) => {
      const p = gl.createProgram();
      gl.attachShader(p, compile(gl.VERTEX_SHADER, vs)); gl.attachShader(p, compile(gl.FRAGMENT_SHADER, fs));
      attribs.forEach((a, i) => gl.bindAttribLocation(p, i, a));
      gl.linkProgram(p);
      if (!gl.getProgramParameter(p, gl.LINK_STATUS)) throw new Error('Link: ' + gl.getProgramInfoLog(p));
      const u = {}; const n = gl.getProgramParameter(p, gl.ACTIVE_UNIFORMS);
      for (let i = 0; i < n; i++) { const info = gl.getActiveUniform(p, i); u[info.name.replace(/\[0\]$/, '')] = gl.getUniformLocation(p, info.name); }
      return { p, u };
    };
    const P = {
      banner: program(VS_BANNER, FS_BANNER, ['aUV']),
      mesh: program(VS_MESH, FS_MESH, ['aPos', 'aNor']),
      shadow: program(VS_SHADOW, FS_SHADOW, ['aP'])
    };

    const buf = (data, target) => { const b = gl.createBuffer(); gl.bindBuffer(target || gl.ARRAY_BUFFER, b); gl.bufferData(target || gl.ARRAY_BUFFER, data, gl.STATIC_DRAW); return b; };
    const meshGeo = (pos, nor, idx) => ({ pos: buf(new Float32Array(pos)), nor: buf(new Float32Array(nor)), idx: buf(new Uint16Array(idx), gl.ELEMENT_ARRAY_BUFFER), count: idx.length });
    const torus = (r, seg, tube) => {
      const pos = [], nor = [], idx = [];
      for (let i = 0; i <= seg; i++) { const th = i / seg * TAU; for (let j = 0; j <= tube; j++) { const ph = j / tube * TAU; const cx = Math.cos(th), sx = Math.sin(th), cp = Math.cos(ph), sp = Math.sin(ph); pos.push((1 + r * cp) * cx, (1 + r * cp) * sx, r * sp); nor.push(cp * cx, cp * sx, sp); } }
      for (let i = 0; i < seg; i++) for (let j = 0; j < tube; j++) { const a = i * (tube + 1) + j, b = a + tube + 1; idx.push(a, b, a + 1, b, b + 1, a + 1); }
      return meshGeo(pos, nor, idx);
    };
    const cylinder = (seg) => {
      const pos = [], nor = [], idx = [];
      for (let i = 0; i <= seg; i++) { const a = i / seg * TAU, c = Math.cos(a), s = Math.sin(a); pos.push(-0.5, c, s, 0.5, c, s); nor.push(0, c, s, 0, c, s); }
      for (let i = 0; i < seg; i++) { const a = i * 2; idx.push(a, a + 1, a + 2, a + 1, a + 3, a + 2); }
      const base = pos.length / 3;
      [-0.5, 0.5].forEach((x, k) => { const ci = pos.length / 3; pos.push(x, 0, 0); nor.push(x < 0 ? -1 : 1, 0, 0); for (let i = 0; i <= seg; i++) { const a = i / seg * TAU; pos.push(x, Math.cos(a), Math.sin(a)); nor.push(x < 0 ? -1 : 1, 0, 0); } for (let i = 0; i < seg; i++) idx.push(ci, ci + 1 + i, ci + 2 + i); });
      return meshGeo(pos, nor, idx);
    };
    const GEO = { ring: torus(0.42, 40, 14), dring: torus(0.14, 36, 10), cyl: cylinder(28) };
    const quad = buf(new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]));

    let grid = null, gridKey = '';
    const buildGrid = (W, H) => {
      const nx = Math.max(30, Math.min(180, Math.round(W * 12))), ny = Math.max(16, Math.min(120, Math.round(H * 12)));
      const key = nx + 'x' + ny;
      if (key === gridKey) return;
      const uv = new Float32Array((nx + 1) * (ny + 1) * 2); let k = 0;
      for (let j = 0; j <= ny; j++) for (let i = 0; i <= nx; i++) { uv[k++] = i / nx; uv[k++] = j / ny; }
      const idx = new Uint16Array(nx * ny * 6); k = 0;
      for (let j = 0; j < ny; j++) for (let i = 0; i < nx; i++) { const a = j * (nx + 1) + i, b = a + 1, c = a + nx + 1, d = c + 1; idx[k++] = a; idx[k++] = b; idx[k++] = c; idx[k++] = b; idx[k++] = d; idx[k++] = c; }
      if (grid) { gl.deleteBuffer(grid.uv); gl.deleteBuffer(grid.idx); }
      grid = { uv: buf(uv), idx: buf(idx, gl.ELEMENT_ARRAY_BUFFER), count: idx.length };
      gridKey = key;
    };

    const mkTex = () => { const t = gl.createTexture(); gl.bindTexture(gl.TEXTURE_2D, t); gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 1, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE, new Uint8Array([240, 240, 238, 255])); return t; };
    const tFront = mkTex(), tBack = mkTex();
    const upload = (t, cv) => {
      gl.bindTexture(gl.TEXTURE_2D, t);
      gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, cv);
      gl.generateMipmap(gl.TEXTURE_2D);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      if (aniso) gl.texParameterf(gl.TEXTURE_2D, aniso.TEXTURE_MAX_ANISOTROPY_EXT, Math.min(8, gl.getParameter(aniso.MAX_TEXTURE_MAX_ANISOTROPY_EXT)));
      gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
    };

    const cvF = document.createElement('canvas'), cvB = document.createElement('canvas');
    const pot = (x) => Math.pow(2, Math.max(8, Math.min(11, Math.round(Math.log2(Math.max(1, x))))));
    const lin = (hex) => { const n = parseInt(hex.slice(1), 16); return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255].map((v) => Math.pow(v, 2.2)); };

    const holesFor = (c, Wi, Hi) => {
      const ins = 0.75, out = [], seen = {};
      const add = (x, y) => { const k = Math.round(x * 10) + ':' + Math.round(y * 10); if (seen[k]) return; seen[k] = 1; out.push([x, y]); };
      const run = (len) => { const n = Math.max(1, Math.ceil((len - 2 * ins) / 24)); const a = []; for (let i = 0; i <= n; i++) a.push(ins + i * (len - 2 * ins) / n); return a; };
      const g = c.grommet;
      if (g === 'all' || g === 'tb') run(Wi).forEach((x) => { add(x, ins); add(x, Hi - ins); });
      if (g === 'all' || g === 'lr') run(Hi).forEach((y) => { add(ins, y); add(Wi - ins, y); });
      if (g === 'corners') { add(ins, ins); add(Wi - ins, ins); add(ins, Hi - ins); add(Wi - ins, Hi - ins); }
      return out;
    };

    const fitText = (ctx, text, maxW, maxH, weight) => {
      ctx.font = `${weight} 100px "Barlow Condensed", "Arial Narrow", Arial, sans-serif`;
      const w = ctx.measureText(text).width || 1;
      const size = Math.min(maxH, maxW * 100 / w);
      ctx.font = `${weight} ${size}px "Barlow Condensed", "Arial Narrow", Arial, sans-serif`;
      return size;
    };

    const drawSample = (ctx, Wi, Hi) => {
      const g = ctx.createLinearGradient(0, 0, Wi, Hi);
      g.addColorStop(0, '#173363'); g.addColorStop(1, '#0B1834');
      ctx.fillStyle = g; ctx.fillRect(0, 0, Wi, Hi);
      ctx.save();
      ctx.globalAlpha = 0.08; ctx.fillStyle = '#FFFFFF';
      for (let x = -Hi; x < Wi; x += 10) { ctx.beginPath(); ctx.moveTo(x, Hi); ctx.lineTo(x + Hi, 0); ctx.lineTo(x + Hi + 3, 0); ctx.lineTo(x + 3, Hi); ctx.closePath(); ctx.fill(); }
      ctx.restore();
      ctx.fillStyle = '#DA1F26';
      ctx.beginPath(); ctx.moveTo(0, Hi * 0.84); ctx.lineTo(Wi, Hi * 0.7); ctx.lineTo(Wi, Hi); ctx.lineTo(0, Hi); ctx.closePath(); ctx.fill();
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      const tall = Hi > Wi * 1.15;
      ctx.fillStyle = '#FFFFFF';
      if (tall) {
        fitText(ctx, 'GRAND', Wi * 0.8, Hi * 0.2, 700); ctx.fillText('GRAND', Wi / 2, Hi * 0.24);
        fitText(ctx, 'OPENING', Wi * 0.86, Hi * 0.2, 700); ctx.fillText('OPENING', Wi / 2, Hi * 0.42);
        ctx.fillStyle = '#FFC93C'; fitText(ctx, 'THIS SATURDAY', Wi * 0.7, Hi * 0.07, 700); ctx.fillText('THIS SATURDAY', Wi / 2, Hi * 0.57);
        ctx.fillStyle = '#FFFFFF'; fitText(ctx, 'YOUR BUSINESS NAME', Wi * 0.7, Hi * 0.05, 600); ctx.fillText('YOUR BUSINESS NAME', Wi / 2, Hi * 0.9);
      } else {
        fitText(ctx, 'GRAND OPENING', Wi * 0.84, Hi * 0.36, 700); ctx.fillText('GRAND OPENING', Wi / 2, Hi * 0.36);
        ctx.fillStyle = '#FFC93C'; fitText(ctx, 'THIS SATURDAY \u00B7 10AM\u20134PM', Wi * 0.6, Hi * 0.11, 700); ctx.fillText('THIS SATURDAY \u00B7 10AM\u20134PM', Wi / 2, Hi * 0.62);
        ctx.fillStyle = '#FFFFFF'; fitText(ctx, 'YOUR BUSINESS NAME  \u00B7  (425) 555-0123', Wi * 0.56, Hi * 0.075, 600); ctx.fillText('YOUR BUSINESS NAME  \u00B7  (425) 555-0123', Wi / 2, Hi * 0.9);
      }
    };

    const drawArt = (ctx, Wi, Hi, face) => {
      if (self.paintArt) { try { if (self.paintArt(ctx, Wi, Hi, face)) return; } catch (e) {} }
      const img = self.artImage;
      if (img && img.width) {
        ctx.fillStyle = '#FFFFFF'; ctx.fillRect(0, 0, Wi, Hi);
        const ia = img.width / img.height, ba = Wi / Hi;
        let dw = Wi, dh = Hi, dx = 0, dy = 0;
        if (ia > ba) { dh = Hi; dw = Hi * ia; dx = (Wi - dw) / 2; } else { dw = Wi; dh = Wi / ia; dy = (Hi - dh) / 2; }
        ctx.drawImage(img, dx, dy, dw, dh);
      } else drawSample(ctx, Wi, Hi);
    };

    const edgeBands = (c) => {
      const P = c.poleSize + 0.5;
      return { top: c.pole !== 'none' ? P : (c.hem ? 1 : 0), bottom: c.pole === 'tb' ? P : (c.hem ? 1 : 0), side: c.hem ? 1 : 0 };
    };

    const drawEdges = (ctx, c, Wi, Hi, face) => {
      const b = edgeBands(c);
      const sides = [['top', b.top], ['bottom', b.bottom], ['left', b.side], ['right', b.side]];
      sides.forEach(([side, d]) => {
        if (!d) return;
        ctx.save();
        let gx0, gy0, gx1, gy1, rect;
        if (side === 'top') { rect = [0, 0, Wi, d]; gx0 = 0; gy0 = 0; gx1 = 0; gy1 = d; }
        if (side === 'bottom') { rect = [0, Hi - d, Wi, d]; gx0 = 0; gy0 = Hi; gx1 = 0; gy1 = Hi - d; }
        if (side === 'left') { rect = [0, 0, d, Hi]; gx0 = 0; gy0 = 0; gx1 = d; gy1 = 0; }
        if (side === 'right') { rect = [Wi - d, 0, d, Hi]; gx0 = Wi; gy0 = 0; gx1 = Wi - d; gy1 = 0; }
        if (face === 'back') { ctx.fillStyle = c.mat.mesh ? '#DCDAD5' : '#E8E6E1'; ctx.globalAlpha = 0.85; ctx.fillRect(rect[0], rect[1], rect[2], rect[3]); ctx.globalAlpha = 1; }
        const g = ctx.createLinearGradient(gx0, gy0, gx1, gy1);
        g.addColorStop(0, 'rgba(0,0,0,0.10)'); g.addColorStop(0.55, 'rgba(0,0,0,0.0)'); g.addColorStop(0.92, 'rgba(255,255,255,0.10)'); g.addColorStop(1, 'rgba(0,0,0,0.28)');
        ctx.fillStyle = g; ctx.fillRect(rect[0], rect[1], rect[2], rect[3]);
        const s = d - 0.32;
        ctx.setLineDash([0.26, 0.13]);
        ctx.lineWidth = 0.045;
        const line = (off, color) => {
          ctx.strokeStyle = color; ctx.beginPath();
          if (side === 'top') { ctx.moveTo(0.3, s + off); ctx.lineTo(Wi - 0.3, s + off); }
          if (side === 'bottom') { ctx.moveTo(0.3, Hi - s + off); ctx.lineTo(Wi - 0.3, Hi - s + off); }
          if (side === 'left') { ctx.moveTo(s + off, 0.3); ctx.lineTo(s + off, Hi - 0.3); }
          if (side === 'right') { ctx.moveTo(Wi - s + off, 0.3); ctx.lineTo(Wi - s + off, Hi - 0.3); }
          ctx.stroke();
        };
        line(0.03, 'rgba(0,0,0,0.35)');
        line(0, 'rgba(255,255,255,0.85)');
        if (c.rope !== 'none' && (side === 'top' || side === 'bottom')) {
          ctx.setLineDash([]);
          const rg = ctx.createLinearGradient(gx0, gy0, gx1, gy1);
          rg.addColorStop(0, 'rgba(255,255,255,0.18)'); rg.addColorStop(0.35, 'rgba(255,255,255,0.0)'); rg.addColorStop(0.5, 'rgba(0,0,0,0.12)');
          ctx.fillStyle = rg; ctx.fillRect(rect[0], rect[1], rect[2], rect[3]);
        }
        ctx.restore();
      });
      if (face === 'back' && c.webbing !== 'none') {
        ctx.save();
        ctx.fillStyle = '#FBFBF9';
        const w = 1;
        ctx.fillRect(0, 0, Wi, w); ctx.fillRect(0, Hi - w, Wi, w); ctx.fillRect(0, 0, w, Hi); ctx.fillRect(Wi - w, 0, w, Hi);
        ctx.strokeStyle = 'rgba(0,0,0,0.12)'; ctx.lineWidth = 0.03; ctx.setLineDash([0.22, 0.12]);
        ctx.strokeRect(0.18, 0.18, Wi - 0.36, Hi - 0.36); ctx.strokeRect(0.82, 0.82, Wi - 1.64, Hi - 1.64);
        ctx.restore();
      }
    };

    const drawCorners = (ctx, Wi, Hi) => {
      const L = 4.5;
      [[0, 0, 1, 1], [Wi, 0, -1, 1], [0, Hi, 1, -1], [Wi, Hi, -1, -1]].forEach(([x, y, sx, sy]) => {
        ctx.save();
        ctx.beginPath(); ctx.moveTo(x, y); ctx.lineTo(x + sx * L, y); ctx.lineTo(x, y + sy * L); ctx.closePath();
        ctx.fillStyle = 'rgba(255,255,255,0.10)'; ctx.fill();
        ctx.strokeStyle = 'rgba(0,0,0,0.18)'; ctx.lineWidth = 0.06; ctx.stroke();
        ctx.beginPath(); ctx.moveTo(x + sx * L * 0.82, y + sy * 0.25); ctx.lineTo(x + sx * 0.25, y + sy * L * 0.82);
        ctx.setLineDash([0.26, 0.13]); ctx.strokeStyle = 'rgba(255,255,255,0.7)'; ctx.lineWidth = 0.045; ctx.stroke();
        ctx.restore();
      });
    };

    const slitsFor = (Wi, Hi) => {
      const out = []; let row = 0;
      for (let y = 12; y <= Hi - 12; y += 20) { const off = (row++ % 2) * 12; for (let x = 14 + off; x <= Wi - 14; x += 24) out.push([x, y]); }
      return out;
    };

    const cutHoles = (ctx, c, Wi, Hi, holes, slits, face) => {
      ctx.save();
      if (face === 'front' && slits.length) {
        ctx.strokeStyle = 'rgba(0,0,0,0.30)'; ctx.lineWidth = 0.18;
        slits.forEach(([x, y]) => { ctx.beginPath(); ctx.arc(x, y + 0.12, 3, 0.12 * PI, 0.88 * PI); ctx.stroke(); });
      }
      ctx.globalCompositeOperation = 'destination-out';
      ctx.fillStyle = '#000';
      holes.forEach(([x, y]) => { ctx.beginPath(); ctx.arc(x, y, 0.24, 0, TAU); ctx.fill(); });
      ctx.strokeStyle = '#000'; ctx.lineWidth = 0.12;
      slits.forEach(([x, y]) => { ctx.beginPath(); ctx.arc(x, y, 3, 0.12 * PI, 0.88 * PI); ctx.stroke(); });
      ctx.restore();
    };

    const redraw = (c) => {
      const Wi = c.W * 12, Hi = c.H * 12;
      let CW, CH;
      if (c.W >= c.H) { CW = 2048; CH = pot(2048 * c.H / c.W); } else { CH = 2048; CW = pot(2048 * c.W / c.H); }
      [cvF, cvB].forEach((cv) => { if (cv.width !== CW) cv.width = CW; if (cv.height !== CH) cv.height = CH; });
      const holes = holesFor(c, Wi, Hi);
      const slits = c.slits ? slitsFor(Wi, Hi) : [];
      const f = cvF.getContext('2d');
      f.setTransform(1, 0, 0, 1, 0, 0); f.clearRect(0, 0, CW, CH);
      f.setTransform(CW / Wi, 0, 0, CH / Hi, 0, 0);
      drawArt(f, Wi, Hi, 'front');
      drawEdges(f, c, Wi, Hi, 'front');
      if (c.corners) drawCorners(f, Wi, Hi);
      cutHoles(f, c, Wi, Hi, holes, slits, 'front');
      const b = cvB.getContext('2d');
      b.setTransform(1, 0, 0, 1, 0, 0); b.clearRect(0, 0, CW, CH);
      b.setTransform(CW / Wi, 0, 0, CH / Hi, 0, 0);
      b.fillStyle = c.mat.backCol; b.fillRect(0, 0, Wi, Hi);
      if (c.sides === 2) { b.save(); b.translate(Wi, 0); b.scale(-1, 1); drawArt(b, Wi, Hi, 'back'); b.restore(); }
      else if (c.mat.backShow > 0) { b.save(); b.globalAlpha = c.mat.backShow; drawArt(b, Wi, Hi, 'front'); b.restore(); }
      drawEdges(b, c, Wi, Hi, 'back');
      if (c.corners) drawCorners(b, Wi, Hi);
      cutHoles(b, c, Wi, Hi, holes, slits, 'back');
      upload(tFront, cvF); upload(tBack, cvB);
      return holes.map(([x, y]) => [x / Wi, 1 - y / Hi]);
    };

    let cur = null, artKey = '', holesUV = [], raf = 0, alive = true, last = performance.now();
    let fontsReady = false;
    if (document.fonts && document.fonts.load) {
      Promise.all([document.fonts.load('700 64px "Barlow Condensed"'), document.fonts.load('600 64px "Barlow Condensed"')]).then(() => { fontsReady = true; artKey = ''; }).catch(() => {});
    }

    const dispAt = (c, amp, t, u, v) => {
      const W = c.W, H = c.H, L = Math.max(W, H), x = (u - 0.5) * W, y = (v - 0.5) * H;
      const d = Math.sin(x * TAU / (L * 1.1) + t * 0.9) * 0.55 + Math.sin(x * TAU / (L * 0.55) + y * TAU / (L * 0.8) - t * 1.25) * 0.3 + Math.sin(y * TAU / (L * 0.7) + x * TAU / (L * 1.6) + t * 0.7) * 0.15;
      let pin = 1;
      const band = (c.poleSize + 0.5) / 12 / H;
      if (c.pole !== 'none') pin *= Math.max(0, Math.min(1, (1 - v - band) / 0.45));
      if (c.pole === 'tb') pin *= Math.max(0, Math.min(1, (v - band) / 0.45));
      let r = 0;
      const rip = c.mat.rip || 0;
      if (rip > 0) {
        const ed = Math.min(Math.min(u * W, (1 - u) * W), Math.min(v * H, (1 - v) * H));
        r = rip * 0.011 * Math.max(0, Math.min(1, ed / 0.3)) * (0.6 * Math.sin(x * TAU / 0.85 + y * TAU / 2.6 + 0.4 * Math.sin(y * TAU / 1.3)) + 0.4 * Math.sin(x * TAU / 0.47 - y * TAU / 1.9 + 1.7));
      }
      return amp * d * pin + r * pin;
    };
    const surf = (c, amp, t, u, v) => {
      const e = 0.004;
      const z = dispAt(c, amp, t, u, v);
      const dx = (dispAt(c, amp, t, u + e, v) - dispAt(c, amp, t, u - e, v)) / (2 * e * c.W);
      const dy = (dispAt(c, amp, t, u, v + e) - dispAt(c, amp, t, u, v - e)) / (2 * e * c.H);
      const l = Math.hypot(dx, dy, 1);
      return { p: [(u - 0.5) * c.W, (v - 0.5) * c.H, z], n: [-dx / l, -dy / l, 1 / l] };
    };

    const drawMesh = (geo, model, rot, color, rough, metal, cam, proj, view) => {
      const pr = P.mesh;
      gl.uniformMatrix4fv(pr.u.uModel, false, model);
      gl.uniformMatrix3fv(pr.u.uNorm, false, M.nmat(rot));
      gl.uniform3fv(pr.u.uColor, color);
      gl.uniform1f(pr.u.uRough, rough);
      gl.uniform1f(pr.u.uMetal, metal);
      gl.bindBuffer(gl.ARRAY_BUFFER, geo.pos); gl.vertexAttribPointer(0, 3, gl.FLOAT, false, 0, 0);
      gl.bindBuffer(gl.ARRAY_BUFFER, geo.nor); gl.vertexAttribPointer(1, 3, gl.FLOAT, false, 0, 0);
      gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, geo.idx);
      gl.drawElements(gl.TRIANGLES, geo.count, gl.UNSIGNED_SHORT, 0);
    };

    const NICKEL = lin('#C9CBCC'), STEEL = lin('#B8BBBE'), ROPE = lin('#E9E4D6');

    let visible = true;
    if (typeof IntersectionObserver !== 'undefined') { try { new IntersectionObserver((es) => { visible = es[0].isIntersecting; }).observe(canvas); } catch (e) {} }
    const frame = (now, snap) => { // snap: one still picture for the proof sheet (see snapshot below)
      if (!alive) return;
      if (!snap) raf = requestAnimationFrame(frame);
      const c = cur; if (!c || (!snap && (!visible || document.hidden))) return;
      const t = now / 1000;
      const dt = snap ? 0 : Math.min(0.05, (now - last) / 1000); if (!snap) last = now;
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      const cw = snap ? snap.w : Math.max(1, Math.round(canvas.clientWidth * dpr)), ch = snap ? snap.h : Math.max(1, Math.round(canvas.clientHeight * dpr));
      if (canvas.width !== cw || canvas.height !== ch) { canvas.width = cw; canvas.height = ch; }
      const key = [c.W, c.H, c.mat.id, c.sides, c.pole, c.poleSize, c.grommet, c.rope, c.webbing, c.corners, c.slits, c.hem, c.artVer, fontsReady].join('|');
      if (key !== artKey) { artKey = key; holesUV = redraw(c); }
      buildGrid(c.W, c.H);

      const v = self.view;
      v.flip += (v.flipTarget - v.flip) * Math.min(1, dt * 5);
      const idleW = Math.max(0, Math.min(1, ((now - v.lastInput) / 1000 - 2.5) / 2));
      const yaw = snap ? snap.yaw : v.yaw + v.flip + idleW * 0.2 * Math.sin(t * 0.33);
      const L = Math.max(c.W, c.H);
      const amp = (c.wind && !snap) ? c.mat.sway * Math.max(0.03, Math.min(0.4, 0.022 * L)) : 0;
      const bob = snap ? 0 : (c.wind ? 1 : 0.5) * 0.025 * Math.max(c.H, L * 0.4) * Math.sin(t * 0.8);
      const model = M.mul(M.trans(0, bob, 0), M.mul(M.rotY(yaw), M.rotX(-(snap ? snap.pitch : v.pitch))));

      const fov = 30 * PI / 180, asp = cw / ch, tf = Math.tan(fov / 2);
      const gap = Math.max(0.42 * c.H, 0.6);
      const groundY = -c.H / 2 - gap;
      const targetY = (c.H / 2 + groundY) / 2;
      const distW = (c.W * 0.64) / (tf * asp);
      const distH = ((c.H / 2 - groundY) * 0.62) / tf;
      const D = Math.max(distW, distH, 2) / (snap ? 1 : v.zoom);
      const proj = M.persp(fov, asp, D * 0.02, D * 12);
      const view = M.trans(0, -targetY, -D);
      const cam = [0, targetY, D];
      const pxPerFt = ch / (2 * D * tf);

      gl.viewport(0, 0, cw, ch);
      gl.clearColor(0, 0, 0, 0);
      gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
      gl.enable(gl.BLEND); gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);

      // contact shadow
      gl.disable(gl.DEPTH_TEST); gl.depthMask(false);
      gl.useProgram(P.shadow.p);
      gl.uniformMatrix4fv(P.shadow.u.uProj, false, proj); gl.uniformMatrix4fv(P.shadow.u.uView, false, view);
      gl.uniform3f(P.shadow.u.uC, 0, groundY, 0);
      gl.uniform2f(P.shadow.u.uR, c.W * 0.56, Math.max(0.5, L * 0.13));
      gl.uniform1f(P.shadow.u.uYaw, yaw);
      const shO = c.mat.mesh ? 0.12 : (c.mat.backlit && c.lights ? 0.3 : 0.22);
      gl.uniform1f(P.shadow.u.uO, shO * (1 - bob / (L * 0.2 + 1)));
      gl.bindBuffer(gl.ARRAY_BUFFER, quad); gl.enableVertexAttribArray(0); gl.disableVertexAttribArray(1);
      gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);

      gl.enable(gl.DEPTH_TEST); gl.depthMask(true);

      // hardware: grommets, D-rings, poles, rope tails
      gl.useProgram(P.mesh.p);
      gl.uniformMatrix4fv(P.mesh.u.uProj, false, proj); gl.uniformMatrix4fv(P.mesh.u.uView, false, view);
      gl.uniform3fv(P.mesh.u.uCam, cam);
      gl.enableVertexAttribArray(0); gl.enableVertexAttribArray(1);
      const R = 0.031;
      holesUV.forEach(([u, vv]) => {
        const s = surf(c, amp, t, u, vv);
        const rot = M.alignZ(s.n);
        [[0.0035, 1], [-0.0035, 0.82]].forEach(([off, k]) => {
          const pos = [s.p[0] + s.n[0] * off, s.p[1] + s.n[1] * off, s.p[2] + s.n[2] * off];
          const local = M.mul(M.trans(pos[0], pos[1], pos[2]), M.mul(rot, M.scale(R * k, R * k, R * 0.55)));
          drawMesh(GEO.ring, M.mul(model, local), M.mul(model, rot), NICKEL, 0.28, 1, cam, proj, view);
        });
      });
      if (c.webbing === 'wd') {
        [[0, 0, -1, -1], [1, 0, 1, -1], [0, 1, -1, 1], [1, 1, 1, 1]].forEach(([u, vv, sx, sy]) => {
          const s = surf(c, amp, t, u, vv);
          const rot = M.alignZ(s.n);
          const off = 0.045;
          const local = M.mul(M.trans(s.p[0] + sx * off, s.p[1] + sy * off, s.p[2]), M.mul(rot, M.scale(0.055, 0.04, 0.055)));
          drawMesh(GEO.dring, M.mul(model, local), M.mul(model, rot), STEEL, 0.3, 1, cam, proj, view);
        });
      }
      if (c.rope !== 'none') {
        const rr = c.rope === 'r5' ? 0.013 : 0.0078;
        [[0, 1, -1], [1, 1, 1], [0, 0, -1], [1, 0, 1]].forEach(([u, vv, sx]) => {
          const s = surf(c, amp, t, u, vv);
          const yIn = vv > 0.5 ? -0.04 : 0.04;
          const local = M.mul(M.trans(s.p[0] + sx * 0.2, s.p[1] + yIn - 0.06, s.p[2]), M.mul(M.rotZ(sx * -0.55), M.scale(0.42, rr, rr)));
          drawMesh(GEO.cyl, M.mul(model, local), M.mul(model, M.rotZ(sx * -0.55)), ROPE, 0.85, 0, cam, proj, view);
        });
      }
      if (c.pole !== 'none') {
        const r = (c.poleSize / PI) / 12;
        const edges = c.pole === 'tb' ? [1, -1] : [1];
        edges.forEach((sgn) => {
          const y = sgn * (c.H / 2 - r);
          const sleeve = M.mul(M.trans(0, y, -r * 1.06), M.scale(c.W - 0.02, r, r));
          drawMesh(GEO.cyl, M.mul(model, sleeve), model, lin(c.mat.backCol), 0.7, 0, cam, proj, view);
          const pole = M.mul(M.trans(0, y, -r * 1.06), M.scale(c.W + 0.6, r * 0.62, r * 0.62));
          drawMesh(GEO.cyl, M.mul(model, pole), model, STEEL, 0.35, 1, cam, proj, view);
        });
      }

      // banner
      const pb = P.banner;
      gl.useProgram(pb.p);
      gl.uniformMatrix4fv(pb.u.uProj, false, proj); gl.uniformMatrix4fv(pb.u.uView, false, view); gl.uniformMatrix4fv(pb.u.uModel, false, model);
      gl.uniform2f(pb.u.uSize, c.W, c.H);
      gl.uniform1f(pb.u.uTime, t); gl.uniform1f(pb.u.uAmp, amp);
      gl.uniform1f(pb.u.uPinTop, c.pole !== 'none' ? 1 : 0); gl.uniform1f(pb.u.uPinBot, c.pole === 'tb' ? 1 : 0);
      const bandV = (c.poleSize + 0.5) / 12 / c.H; gl.uniform1f(pb.u.uBandT, bandV); gl.uniform1f(pb.u.uBandB, bandV);
      gl.uniform3fv(pb.u.uCam, cam);
      gl.uniform3f(pb.u.uTX, model[0], model[1], model[2]); gl.uniform3f(pb.u.uTY, model[4], model[5], model[6]);
      const m = c.mat;
      gl.uniform1f(pb.u.uRough, m.rough); gl.uniform1f(pb.u.uSheen, m.sheen); gl.uniform1f(pb.u.uWrap, m.wrap); gl.uniform1f(pb.u.uRip, m.rip || 0);
      const weavePx = pxPerFt / m.weaveFreq;
      gl.uniform1f(pb.u.uWeave, m.weaveAmt * Math.max(0, Math.min(1, (weavePx - 1.4) / 3)));
      gl.uniform1f(pb.u.uWeaveType, m.weave); gl.uniform1f(pb.u.uWeaveFreq, m.weaveFreq);
      const meshFreq = 40;
      gl.uniform1f(pb.u.uMesh, m.mesh ? 1 : 0); gl.uniform1f(pb.u.uMeshFreq, meshFreq);
      gl.uniform1f(pb.u.uMeshFade, 1 - Math.max(0, Math.min(1, (pxPerFt / meshFreq - 2) / 3)));
      gl.uniform1f(pb.u.uHemIn, c.hem ? 1.05 : 0);
      gl.uniform1f(pb.u.uEmit, m.backlit && c.lights ? 0.6 : 0);
      gl.activeTexture(gl.TEXTURE0); gl.bindTexture(gl.TEXTURE_2D, tFront); gl.uniform1i(pb.u.uFront, 0);
      gl.activeTexture(gl.TEXTURE1); gl.bindTexture(gl.TEXTURE_2D, tBack); gl.uniform1i(pb.u.uBack, 1);
      gl.disableVertexAttribArray(1);
      gl.enableVertexAttribArray(0);
      gl.bindBuffer(gl.ARRAY_BUFFER, grid.uv); gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
      gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, grid.idx);
      gl.disable(gl.CULL_FACE);
      gl.drawElements(gl.TRIANGLES, grid.count, gl.UNSIGNED_SHORT, 0);
    };

    raf = requestAnimationFrame(frame);
    return {
      sync(c) { cur = c; },
      redraw() { artKey = ''; },
      stop() { alive = false; cancelAnimationFrame(raf); },
// proof sheet (ardor-proofsheet.js): a still picture from a named view ('home', 'front' or 'back'), wind off, on white
snapshot(w, h, name) {
if (!alive || !cur) return null;
try {
w = Math.max(16, Math.round(w || 800)); h = Math.max(16, Math.round(h || 600));
const yaw = name === 'back' ? PI + 0.35 : (name === 'front' ? 0 : 0.35);
frame(performance.now(), { w: w, h: h, yaw: yaw, pitch: 0.08 });
const out = document.createElement('canvas'); out.width = w; out.height = h;
const ctx = out.getContext('2d'); ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, w, h); ctx.drawImage(canvas, 0, 0, w, h);
return out;
} catch (e) { return null; }
},
      _gl: gl
    };
  }
  window.ArdorBanner3D = { create: createEngine };
})();
